import { useRef, ReactNode, useState, useCallback } from 'react';
import { BookmarkIcon, FileTextIcon, LoaderCircleIcon, ZapIcon } from 'lucide-react';
import { motion } from 'motion/react';
import type { Brief } from '@/entities/brief';
import type { ProjectListItem } from '@/entities/project';
import { OpportunityCardItem, OpportunityDetailOverlay } from '@/entities/opportunity';
import {
  BriefIntakeModal,
  BriefGenerationProgressModal,
  type BriefIntakeValues,
} from '@/features/brief-generation';
import { useBriefAgentReview } from '@/features/brief-agent-review';
import { useOpportunitiesRoomLayout } from '@/features/opportunities-room/model/useOpportunitiesRoomLayout';
import { OpportunitiesKpiStrip } from '@/features/opportunities-room/ui/OpportunitiesKpiStrip';
import { OpportunitiesRoomToolbar } from '@/features/opportunities-room/ui/OpportunitiesRoomToolbar';
import { CardGrid } from '@/features/opportunities-room/ui/CardGrid';
import { AboutPageDialog, SectionHeader } from '@/shared/ui';
import { StaggerItem } from '@/shared/ui/animations';
import {
  PageHeader,
  PageHeaderIdentity,
  PageHeaderTitle,
  PageHeaderToolGroup,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { useCurrentWorkspaceId } from '@/entities/workspace/model/workspace.store';
import { useTranslation } from 'react-i18next';

export interface OpportunitiesRoomLayoutProps {
  renderDetail?: (item: unknown, onClose?: () => void) => ReactNode;
  onSelectItem?: (item: unknown) => void;
}

export function OpportunitiesRoomLayout({
  renderDetail,
  onSelectItem,
}: OpportunitiesRoomLayoutProps = {}) {
  const { t } = useTranslation('analysis');

  // Agent brief review (POC) — fire-and-forget review of freshly created briefs.
  const { review } = useBriefAgentReview();
  const workspaceId = useCurrentWorkspaceId();

  const [isAboutOpen, setIsAboutOpen] = useState(false);

  // Brief generation state
  const [intakeModalOpen, setIntakeModalOpen] = useState(false);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [generationPayload, setGenerationPayload] = useState<BriefIntakeValues | null>(null);
  // The platform id rather than the list item: it is what the project-scoped
  // brief routes accept, and it doubles as the "a project is selected" signal.
  const [briefPlatformProjectId, setBriefPlatformProjectId] = useState<string | null>(null);

  const {
    kpi,
    isLoading,
    isKpiLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    toggleFavorite,
    flashOpportunities,
    otherOpportunities,
    filters,
    opportunityType,
    radarlistOnly,
    marketOptions,
    audienceOptions,
    setSearch,
    setInsightType,
    setMarket,
    setAudience,
    setOpportunityType,
    setRadarlistOnly,
    overlayState,
    closeOverlay,
    handleCardClick,
    handleOpenDetail,
    isNew,
  } = useOpportunitiesRoomLayout();

  const showFlash = isLoading || flashOpportunities.length > 0;
  const showOther = isLoading || otherOpportunities.length > 0;
  const showEmpty =
    !isLoading && flashOpportunities.length === 0 && otherOpportunities.length === 0;

  // Brief generation callbacks
  const handleGenerate = useCallback((payload: BriefIntakeValues) => {
    setGenerationPayload(payload);
    setIntakeModalOpen(false);
    setProgressModalOpen(true);
  }, []);

  const handleGenerationSuccess = useCallback(
    (brief: Brief) => {
      if (!briefPlatformProjectId) return;

      // Agent brief review (POC) — fire-and-forget on creation.
      void review(brief, briefPlatformProjectId);

      // Close modals and reset state
      setProgressModalOpen(false);
      setGenerationPayload(null);
      setBriefPlatformProjectId(null);

      // This must run inside a click or keypress event listener
      const openAndFocusTab = (url: string) => {
        const newTab = window.open(url, '_blank', 'noopener,noreferrer');
        if (newTab) {
          newTab.focus();
        }
      };

      // Open the new brief in a new tab (allows user to continue reviewing opportunities)
      const briefUrl = `${window.location.origin}${ROUTES.PROJECT_BRIEF_DETAIL(briefPlatformProjectId, brief.id)}`;
      openAndFocusTab(briefUrl);
    },
    [briefPlatformProjectId, review],
  );

  const handleCloseBriefModals = useCallback(() => {
    setIntakeModalOpen(false);
    setProgressModalOpen(false);
    setGenerationPayload(null);
    setBriefPlatformProjectId(null);
  }, []);

  // Refs so the overlay's project-selected handler can consult the current
  // modal state without re-subscribing on every render. Reading state through
  // refs here (never in render) is safe because `handleProjectSelected` fires
  // from a user event, not from a subscription.
  const intakeModalOpenRef = useRef(intakeModalOpen);
  intakeModalOpenRef.current = intakeModalOpen;
  const progressModalOpenRef = useRef(progressModalOpen);
  progressModalOpenRef.current = progressModalOpen;

  // Overlay project selection — do the work in the handler instead of
  // parking the choice in state and reacting via useEffect. This collapses
  // "set project → effect reads it → opens modal + clears" into one atomic
  // event flow.
  const handleProjectSelected = useCallback((project: ProjectListItem) => {
    if (intakeModalOpenRef.current || progressModalOpenRef.current) return;
    if (!project.platformProjectId) return;
    setBriefPlatformProjectId(project.platformProjectId);
    setIntakeModalOpen(true);
  }, []);

  // Render brief modals via slot pattern - no setState during render
  const renderBriefModals = useCallback(() => {
    return (
      <>
        {/* Brief intake modal */}
        {intakeModalOpen && briefPlatformProjectId && (
          <BriefIntakeModal
            open={intakeModalOpen}
            onClose={handleCloseBriefModals}
            onGenerate={handleGenerate}
          />
        )}

        {/* Brief generation progress modal */}
        {progressModalOpen && generationPayload && briefPlatformProjectId && (
          <BriefGenerationProgressModal
            open={progressModalOpen}
            onClose={handleCloseBriefModals}
            payload={generationPayload}
            projectId={briefPlatformProjectId}
            onSuccess={handleGenerationSuccess}
          />
        )}
      </>
    );
  }, [
    intakeModalOpen,
    progressModalOpen,
    briefPlatformProjectId,
    generationPayload,
    handleGenerate,
    handleGenerationSuccess,
    handleCloseBriefModals,
  ]);

  // Infinite scroll sentinel — attach the IntersectionObserver via a callback
  // ref (skill: "Callback refs for DOM side effects") so setup/teardown ride
  // React 19's callback-ref cleanup return instead of a useEffect that
  // mirrored the same lifecycle.
  //
  // `hasNextPage` is a REAL dependency of the callback, not a ref read. That is
  // load-bearing: the sentinel sits below page 1 and is usually already inside
  // the 200px root margin, so the only intersection signal we ever get is the
  // spec-guaranteed initial callback that `observe()` delivers asynchronously.
  // With a `[]` callback the observer was built exactly once — while page 1 was
  // still in flight and `hasNextPage` was false — so that single callback bailed
  // and nothing ever crossed the threshold again: pagination stalled forever
  // with no scroll room to recover. Keying the callback on `hasNextPage` makes
  // the false -> true flip change the ref identity, so React detaches and
  // re-attaches it on the SAME (stable, unkeyed) sentinel node: old observer
  // disconnected, new one created, fresh initial callback delivered. Churn is
  // bounded — `hasNextPage` only flips at the start and end of pagination, and
  // `fetchNextPage` / `isFetchingNextPage` stay behind refs so page-to-page
  // renders do not re-attach anything.
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  isFetchingNextPageRef.current = isFetchingNextPage;
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const attachSentinel = useCallback(
    (el: HTMLDivElement | null) => {
      // No observer at all when there is nothing left to load; the flip back to
      // `true` re-attaches and re-observes.
      if (!el || !hasNextPage) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          // `isFetchingNextPage` stays a ref guard so a re-attach (or a queued
          // entry) can never stack a second request on an in-flight page.
          if (entry.isIntersecting && !isFetchingNextPageRef.current) {
            void fetchNextPageRef.current();
          }
        },
        { rootMargin: '200px' },
      );
      observer.observe(el);
      return () => observer.disconnect();
    },
    [hasNextPage],
  );

  return (
    <>
      <div data-testid="opportunities-room-layout" className="h-full overflow-y-auto">
        <motion.div
          className="flex flex-col gap-app-inset p-app-inset"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Page header */}
          <StaggerItem index={0}>
            <PageHeader>
              <PageHeaderIdentity>
                <PageHeaderToolGroup toolId="opportunities-room" category="plan">
                  <PageHeaderTitle info={{ onClick: () => setIsAboutOpen(true) }}>
                    {t('opportunitiesRoom.opportunitiesRoomLayout.opportunitiesRoom')}
                  </PageHeaderTitle>
                </PageHeaderToolGroup>
              </PageHeaderIdentity>
            </PageHeader>
          </StaggerItem>

          {/* KPI strip */}
          <StaggerItem index={1}>
            <OpportunitiesKpiStrip kpi={kpi} isLoading={isKpiLoading} />
          </StaggerItem>

          {/* Content */}
          <StaggerItem index={2}>
            <div className="flex flex-col gap-6 rounded-lg bg-card p-section text-foreground">
              <OpportunitiesRoomToolbar
                filters={filters}
                opportunityType={opportunityType}
                radarlistOnly={radarlistOnly}
                marketOptions={marketOptions}
                audienceOptions={audienceOptions}
                onSearch={setSearch}
                onInsightTypeChange={setInsightType}
                onOpportunityTypeChange={setOpportunityType}
                onMarketChange={setMarket}
                onAudienceChange={setAudience}
                onRadarlistOnlyChange={setRadarlistOnly}
              />

              {/* Flash opportunities */}
              {showFlash && (
                <section className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <SectionHeader
                      title={t('opportunitiesRoom.opportunitiesRoomLayout.flashOpportunities')}
                      size="small"
                    />
                    <span className="text-muted-foreground" aria-hidden="true">
                      •
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t('opportunitiesRoom.opportunitiesRoomLayout.shortFusedOpportunitiesToAct')}
                    </span>
                  </div>
                  <CardGrid
                    opportunities={flashOpportunities}
                    isLoading={isLoading}
                    onCardClick={handleCardClick}
                    onToggleFavorite={toggleFavorite}
                    onOpenDetail={handleOpenDetail}
                    isNew={isNew}
                  />
                </section>
              )}

              {/* Other opportunities */}
              {showOther && (
                <section className="flex flex-col gap-4">
                  <div className="flex gap-1 flex-col md:flex-row md:items-center md:gap-2">
                    <h2 className="text-base font-medium text-foreground">
                      {t('opportunitiesRoom.opportunitiesRoomLayout.otherOpportunities')}
                    </h2>
                    <span className="text-muted-foreground hidden md:inline" aria-hidden="true">
                      •
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t('opportunitiesRoom.opportunitiesRoomLayout.rankedByTimeOfGeneration')}
                    </span>
                  </div>
                  <CardGrid
                    opportunities={otherOpportunities}
                    isLoading={isLoading}
                    onCardClick={handleCardClick}
                    onToggleFavorite={toggleFavorite}
                    onOpenDetail={handleOpenDetail}
                    isNew={isNew}
                  />
                </section>
              )}

              {showEmpty && (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <p className="text-sm">
                    {t('opportunitiesRoom.opportunitiesRoomLayout.noOpportunitiesMatchTheCurrent')}
                  </p>
                </div>
              )}

              {/* Infinite scroll sentinel */}
              <div ref={attachSentinel} />
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <LoaderCircleIcon className="size-5 text-muted-foreground animate-spin" />
                </div>
              )}
            </div>
          </StaggerItem>
        </motion.div>
      </div>

      <AboutPageDialog
        open={isAboutOpen}
        onOpenChange={setIsAboutOpen}
        title={t('opportunitiesRoom.opportunitiesRoomLayout.about.title')}
        description={t('opportunitiesRoom.opportunitiesRoomLayout.about.description')}
        rows={[
          {
            icon: ZapIcon,
            text: t('opportunitiesRoom.opportunitiesRoomLayout.about.signalsLandHere'),
          },
          {
            icon: BookmarkIcon,
            text: t('opportunitiesRoom.opportunitiesRoomLayout.about.trackWhatMatters'),
          },
          {
            icon: FileTextIcon,
            text: t('opportunitiesRoom.opportunitiesRoomLayout.about.openAndAct'),
          },
        ]}
      />

      {overlayState && renderDetail && (
        <OpportunityDetailOverlay
          items={overlayState.items}
          initialIndex={overlayState.initialIndex}
          onClose={closeOverlay}
          onItemChange={onSelectItem}
          renderItem={(item) => (
            <div className="overflow-y-auto h-full max-h-[calc(100vh-16px)]">
              {renderDetail(item, closeOverlay)}
            </div>
          )}
          getItemId={(item: OpportunityCardItem) => item.opportunityId}
          getIsFavorite={(item: OpportunityCardItem) => item.isFavorite ?? false}
          onToggleFavorite={toggleFavorite}
          workspaceId={workspaceId}
          onProjectSelected={handleProjectSelected}
          renderBriefModals={renderBriefModals}
        />
      )}
    </>
  );
}
